import { Dropdown, DropdownProps } from 'components/dropdown/Dropdown';
import { dropdownTriggerStyles } from 'pages/Desk/issues/issue_modals/shared';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

export type ProfileDropdownOption<TValue extends string> = {
    Value: TValue;
    Text: string;
};

// pick keys from DropdownProps
type RequiredProps<TValue extends string> = Pick<
    DropdownProps<ProfileDropdownOption<TValue>>,
    'value'
>;
export type ProfileSettingDropdownInlineEditorProps<TValue extends string> = Readonly<{
    options: ProfileDropdownOption<TValue>[];
    onConfirm: DropdownProps<ProfileDropdownOption<TValue>>['onChange'];
}> &
    RequiredProps<TValue> &
    Partial<
        Omit<
            DropdownProps<ProfileDropdownOption<TValue>>,
            keyof RequiredProps<TValue> | 'onChange'
        >
    >;

// wrapped to pass down classname for styled components to trigger, since thats
// what we want to style here
export function DropdownWithStylableTrigger<T>({
    className,
    ...props
}: DropdownProps<T> & { className?: string }) {
    return (
        <Dropdown<T>
            {...props}
            trigger={{
                ...props.trigger,
                className: `${className} ${props.trigger?.className ?? ''}`,
            }}
        />
    );
}

const StyledDropdown = styled(DropdownWithStylableTrigger)`
    border: 1px solid var(--border-default);
    transition: background-color 0.075s;
    &:hover {
        background-color: var(--action-hover) !important;
    }
    &:active {
        background-color: var(--action-selected) !important;
    }
` as typeof DropdownWithStylableTrigger;

export function ProfileSettingDropdown<TValue extends string>(
    props: ProfileSettingDropdownInlineEditorProps<TValue>,
) {
    return (
        <StyledDropdown<ProfileDropdownOption<TValue>>
            renderLabel={({ value }) => value?.Text}
            renderItem={({ item }) => item.Text}
            dataKey={(item) => item.Value}
            data={props.options}
            isDisabled={props.isDisabled}
            popup={{ width: 'max-content' }}
            trigger={{
                style: {
                    ...dropdownTriggerStyles,
                    backgroundColor: tokens.input.background,
                    margin: 0,
                },
            }}
            {...props}
            value={props.value}
            onChange={props.onConfirm}
        />
    );
}
